import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Header from '../components/Header';
import { getMediaURL } from '../utils/storage';
import { Play, Pause, SkipForward, SkipBack, Volume2, Type, Settings as SettingsIcon, Plus, BookOpen, Video, Music as MusicIcon, FastForward } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';

const SPEED_OPTIONS = [0.5, 0.75, 1.0, 1.25, 1.35, 1.5, 1.75, 2.0];

export default function TopicView() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { topics, settings, updateSettings, syncSanskarsInYagya } = useApp();
  const topic = topics.find(t => t.id === topicId);
  const sanskarsTopic = topics.find(t => t.id === 'sanskars');

  const [currentIdx, setCurrentIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMeaning, setShowMeaning] = useState(false);
  const [playMeaningAudio, setPlayMeaningAudio] = useState(false);
  const [audioSrc, setAudioSrc] = useState('');
  const [videoSrc, setVideoSrc] = useState('');
  const [meaningAudioSrc, setMeaningAudioSrc] = useState('');
  const [showSanskarDialog, setShowSanskarDialog] = useState(false);
  const [selectedSanskars, setSelectedSanskars] = useState([]);
  const [gayatriCounter, setGayatriCounter] = useState(0);
  const [pauseAfterEach, setPauseAfterEach] = useState(false);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  const speed = settings.speed || 1.0;
  const fontSize = settings.fontSize || 18;
  const gayatriRepeat = settings.gayatriRepeat || 11;

  // Currently active sanskar groups in this yagya
  const activeSanskarGroups = useMemo(() => {
    if (!topic) return [];
    return [...new Set(topic.mantras.filter(m => m.sanskarGroupSource).map(m => m.sanskarGroupSource))];
  }, [topic]);

  // When opening sanskar dialog, pre-fill with active ones
  const openSanskarDialog = () => {
    setSelectedSanskars(activeSanskarGroups);
    setShowSanskarDialog(true);
  };

  const mantra = topic?.mantras[currentIdx];

  // Load media URLs when mantra changes
  useEffect(() => {
    let revokes = [];
    (async () => {
      if (!mantra) return;
      const a = await getMediaURL(mantra.audioUrl);
      const v = await getMediaURL(mantra.videoUrl);
      const ma = await getMediaURL(mantra.meaningAudioUrl);
      setAudioSrc(a); setVideoSrc(v); setMeaningAudioSrc(ma);
      [a, v, ma].forEach(u => u && revokes.push(u));
    })();
    return () => revokes.forEach(u => URL.revokeObjectURL(u));
    // eslint-disable-next-line
  }, [mantra?.id]);

  // Apply speed to media
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed, audioSrc, videoSrc]);

  const handlePlayPause = () => {
    const el = videoSrc ? videoRef.current : audioRef.current;
    if (!el) {
      toast.info('इस मन्त्र के लिए कोई ऑडियो/वीडियो नहीं जोड़ा गया। Settings में जाकर जोड़ें।');
      return;
    }
    if (isPlaying) { el.pause(); setIsPlaying(false); }
    else { el.play(); setIsPlaying(true); }
  };

  const goNext = () => {
    if (!topic) return;
    if (currentIdx < topic.mantras.length - 1) {
      setCurrentIdx(currentIdx + 1);
      setIsPlaying(false);
    } else {
      setIsPlaying(false);
      toast.success('यज्ञ प्रक्रिया पूर्ण हुई। ॐ शान्तिः');
    }
  };

  const goPrev = () => {
    if (currentIdx > 0) { setCurrentIdx(currentIdx - 1); setIsPlaying(false); }
  };

  // Reset Gayatri counter when mantra changes
  useEffect(() => {
    setGayatriCounter(0);
  }, [mantra?.id]);

  const handleMediaEnded = async () => {
    // Gayatri ahuti repeat logic
    if (mantra?.isGayatriAhuti && gayatriCounter + 1 < gayatriRepeat) {
      const nextCount = gayatriCounter + 1;
      setGayatriCounter(nextCount);
      toast.info(`गायत्री आहुति ${nextCount + 1} / ${gayatriRepeat}`);
      const el = videoSrc ? videoRef.current : audioRef.current;
      if (el) {
        el.currentTime = 0;
        el.play();
      } else {
        // No media - just advance counter and continue after delay
        setTimeout(() => handleMediaEnded(), 100);
      }
      return;
    }
    // Play meaning audio if enabled
    if (playMeaningAudio && meaningAudioSrc) {
      const ma = new Audio(meaningAudioSrc);
      ma.playbackRate = speed;
      ma.play();
      ma.onended = () => handleAfterAudio();
      return;
    }
    handleAfterAudio();
  };

  const handleAfterAudio = () => {
    if (pauseAfterEach || mantra?.pauseAfter) {
      setIsPlaying(false);
      toast.info('विराम। अगले मन्त्र के लिए Next दबाएं।');
    } else {
      goNext();
    }
  };

  const cycleSpeed = () => {
    const idx = SPEED_OPTIONS.indexOf(speed);
    const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length] || 1.0;
    updateSettings({ speed: next });
  };

  const handleAddSanskars = () => {
    syncSanskarsInYagya(selectedSanskars);
    const added = selectedSanskars.filter(id => !activeSanskarGroups.includes(id)).length;
    const removed = activeSanskarGroups.filter(id => !selectedSanskars.includes(id)).length;
    if (added > 0 && removed > 0) {
      toast.success(`${added} संस्कार जोड़े, ${removed} हटाए।`);
    } else if (added > 0) {
      toast.success(`${added} संस्कार जोड़े गए।`);
    } else if (removed > 0) {
      toast.success(`${removed} संस्कार हटाए गए।`);
    } else {
      toast.info('कोई परिवर्तन नहीं।');
    }
    setShowSanskarDialog(false);
  };

  const toggleSanskar = (id) => {
    setSelectedSanskars(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (!topic) {
    return (
      <div className="min-h-screen">
        <Header title="नहीं मिला" showBack />
        <div className="p-6">विषय नहीं मिला।</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header
        title={topic.title}
        subtitle={`${currentIdx + 1} / ${topic.mantras.length} · ${topic.subtitle}`}
        showBack
        rightAction={
          <button
            onClick={() => navigate(`/settings/topic/${topic.id}`)}
            className="p-2 rounded-full hover:bg-white/20 transition-colors"
            aria-label="Edit"
          >
            <SettingsIcon className="w-5 h-5" />
          </button>
        }
      />

      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Mantra navigation chips */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
          {topic.mantras.map((m, idx) => (
            <button
              key={m.id}
              onClick={() => { setCurrentIdx(idx); setIsPlaying(false); }}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors devanagari-ui ${
                idx === currentIdx
                  ? 'bg-orange-600 text-white border-orange-600'
                  : 'bg-white text-orange-900 border-orange-200 hover:bg-orange-50'
              }`}
            >
              {idx + 1}. {m.name}
            </button>
          ))}
        </div>

        {/* Add Sanskar button (Daily Yagya only) */}
        {topic.allowSanskar && sanskarsTopic && (
          <div className="mb-4 flex flex-col sm:flex-row gap-2">
            <button
              onClick={openSanskarDialog}
              className="flex-1 flex items-center justify-center gap-2 bg-pink-50 hover:bg-pink-100 text-pink-700 border border-pink-200 rounded-xl py-3 font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="devanagari-ui">
                संस्कार जोड़ें / हटाएं
                {activeSanskarGroups.length > 0 && (
                  <span className="ml-2 inline-block bg-pink-200 text-pink-800 text-xs px-2 py-0.5 rounded-full">{activeSanskarGroups.length} सक्रिय</span>
                )}
              </span>
            </button>
            <label className="flex items-center justify-center gap-2 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-xl px-4 py-3 cursor-pointer transition-colors">
              <Switch checked={pauseAfterEach} onCheckedChange={setPauseAfterEach} />
              <span className="text-sm font-medium devanagari-ui">हर मन्त्र के बाद विराम</span>
            </label>
          </div>
        )}

        {/* For non-yagya topics, also show pause toggle */}
        {!topic.allowSanskar && (
          <div className="mb-4">
            <label className="flex items-center gap-2 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-xl px-4 py-3 cursor-pointer transition-colors w-fit">
              <Switch checked={pauseAfterEach} onCheckedChange={setPauseAfterEach} />
              <span className="text-sm font-medium devanagari-ui">हर मन्त्र के बाद विराम</span>
            </label>
          </div>
        )}

        {/* Mantra card */}
        <div className="bg-white rounded-2xl shadow-md border border-orange-100 overflow-hidden">
          <div className="saffron-gradient text-white px-5 py-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-bold shrink-0">
              {currentIdx + 1}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold devanagari-ui">{mantra.name}</h2>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {mantra.audioUrl && <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full flex items-center gap-1"><MusicIcon className="w-2.5 h-2.5" /> Audio</span>}
                {mantra.videoUrl && <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full flex items-center gap-1"><Video className="w-2.5 h-2.5" /> Video</span>}
                {mantra.meaning && <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full flex items-center gap-1"><BookOpen className="w-2.5 h-2.5" /> अर्थ</span>}
                {mantra.pauseAfter && <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">विराम</span>}
              </div>
            </div>
          </div>

          {/* Video */}
          {videoSrc && (
            <div className="bg-black">
              <video
                ref={videoRef}
                src={videoSrc}
                controls
                onEnded={handleMediaEnded}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                className="w-full max-h-80"
              />
            </div>
          )}

          {/* Mantra text */}
          <div className="px-6 py-6 om-bg">
            <p
              className="devanagari text-orange-950 whitespace-pre-wrap"
              style={{ fontSize: `${fontSize}px` }}
            >
              {mantra.text}
            </p>
            {showMeaning && mantra.meaning && (
              <div className="mt-4 pt-4 border-t border-orange-100">
                <p className="text-xs font-semibold text-orange-700 mb-1 devanagari-ui">अर्थ:</p>
                <p className="text-stone-700 devanagari-ui" style={{ fontSize: `${fontSize - 2}px` }}>{mantra.meaning}</p>
              </div>
            )}

            {/* Gayatri repeat counter */}
            {mantra.isGayatriAhuti && (
              <div className="mt-5 pt-4 border-t border-orange-100">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <span className="text-sm font-semibold text-orange-800 devanagari-ui">
                    आहुति गणना: {gayatriCounter + 1} / {gayatriRepeat}
                  </span>
                  <div className="flex items-center gap-1.5 bg-white border border-orange-200 rounded-lg px-1.5 py-1">
                    <span className="text-xs text-stone-600 devanagari-ui px-1">कुल बार:</span>
                    <button
                      onClick={() => updateSettings({ gayatriRepeat: Math.max(1, gayatriRepeat - 1) })}
                      className="w-6 h-6 rounded bg-orange-100 hover:bg-orange-200 text-orange-800 font-bold flex items-center justify-center text-sm"
                      aria-label="Decrease"
                    >−</button>
                    <input
                      type="number"
                      min={1}
                      max={108}
                      value={gayatriRepeat}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v) && v >= 1 && v <= 108) updateSettings({ gayatriRepeat: v });
                      }}
                      className="w-12 text-center text-sm font-bold text-orange-800 bg-transparent outline-none"
                    />
                    <button
                      onClick={() => updateSettings({ gayatriRepeat: Math.min(108, gayatriRepeat + 1) })}
                      className="w-6 h-6 rounded bg-orange-100 hover:bg-orange-200 text-orange-800 font-bold flex items-center justify-center text-sm"
                      aria-label="Increase"
                    >+</button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  {!audioSrc && !videoSrc && gayatriCounter + 1 < gayatriRepeat && (
                    <button
                      onClick={() => setGayatriCounter(c => c + 1)}
                      className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-full devanagari-ui"
                    >
                      अगली आहुति +1
                    </button>
                  )}
                  {!audioSrc && !videoSrc && gayatriCounter + 1 >= gayatriRepeat && (
                    <button
                      onClick={() => goNext()}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-full devanagari-ui"
                    >
                      आगे बढ़ें →
                    </button>
                  )}
                  <button
                    onClick={() => setGayatriCounter(0)}
                    className="px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 text-xs font-semibold rounded-full devanagari-ui"
                  >
                    रीसेट
                  </button>
                </div>
                <div className="w-full h-2 bg-orange-100 rounded-full overflow-hidden">
                  <div
                    className="h-full saffron-gradient transition-all"
                    style={{ width: `${((gayatriCounter + 1) / gayatriRepeat) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Hidden audio element */}
          {audioSrc && !videoSrc && (
            <audio
              ref={audioRef}
              src={audioSrc}
              onEnded={handleMediaEnded}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
          )}

          {/* Toggles */}
          <div className="px-5 py-3 border-t border-orange-100 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm bg-orange-50/40">
            {mantra.meaning && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={showMeaning} onCheckedChange={setShowMeaning} />
                <span className="text-stone-700 devanagari-ui">अर्थ दिखाएं</span>
              </label>
            )}
            {mantra.meaningAudioUrl && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={playMeaningAudio} onCheckedChange={setPlayMeaningAudio} />
                <span className="text-stone-700 devanagari-ui">अर्थ ऑडियो चलाएं</span>
              </label>
            )}
          </div>
        </div>

        {/* Font size */}
        <div className="mt-4 bg-white rounded-xl border border-orange-100 p-4">
          <div className="flex items-center gap-3 mb-2">
            <Type className="w-4 h-4 text-orange-700" />
            <span className="text-sm font-medium text-stone-700 devanagari-ui">पाठ आकार</span>
            <span className="text-xs text-stone-500 ml-auto">{fontSize}px</span>
          </div>
          <Slider
            value={[fontSize]}
            min={14} max={32} step={1}
            onValueChange={(v) => updateSettings({ fontSize: v[0] })}
            className="speed-slider"
          />
        </div>
      </div>

      {/* Bottom player bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-orange-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            onClick={goPrev}
            disabled={currentIdx === 0}
            className="p-3 rounded-full hover:bg-orange-50 disabled:opacity-30 disabled:cursor-not-allowed text-orange-900 transition-colors"
            aria-label="Previous"
          >
            <SkipBack className="w-5 h-5" />
          </button>

          <button
            onClick={handlePlayPause}
            className={`w-14 h-14 rounded-full saffron-gradient text-white flex items-center justify-center shadow-md ${isPlaying ? 'pulse-glow' : ''}`}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
          </button>

          <button
            onClick={goNext}
            disabled={currentIdx >= topic.mantras.length - 1}
            className="p-3 rounded-full hover:bg-orange-50 disabled:opacity-30 disabled:cursor-not-allowed text-orange-900 transition-colors"
            aria-label="Next"
          >
            <SkipForward className="w-5 h-5" />
          </button>

          <div className="flex-1" />

          <button
            onClick={cycleSpeed}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-800 text-sm font-semibold transition-colors"
            title="Speed control"
          >
            <FastForward className="w-4 h-4" />
            {speed}x
          </button>
        </div>
      </div>

      {/* Sanskar add dialog */}
      <Dialog open={showSanskarDialog} onOpenChange={setShowSanskarDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="devanagari-ui">संस्कार जोड़ें / हटाएं</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-stone-600 devanagari-ui bg-orange-50 border border-orange-200 rounded-lg p-2.5">
              💡 हर मन्त्र अपनी निर्धारित position पर automatic add होगा। Position बदलने के लिए <strong>Settings → संस्कार क्रम</strong> में जाएं।
            </p>
            <div>
              <p className="text-xs font-semibold text-stone-600 mb-2 devanagari-ui">
                संस्कार चुनें ({selectedSanskars.length} चयनित) ·
                <span className="text-stone-500 font-normal"> चेक हटाने पर हट जाएगा</span>
              </p>
              <ScrollArea className="h-72 border border-orange-100 rounded-lg p-2">
                <div className="space-y-1">
                  {(sanskarsTopic.sanskarGroups || []).map((g) => {
                    const mantraCount = sanskarsTopic.mantras.filter(m => m.sanskarGroup === g.id).length;
                    return (
                      <label key={g.id} className="flex items-start gap-3 p-2 rounded-md hover:bg-orange-50 cursor-pointer">
                        <Checkbox
                          checked={selectedSanskars.includes(g.id)}
                          onCheckedChange={() => toggleSanskar(g.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <span className="text-sm text-stone-800 devanagari-ui block">{g.name}</span>
                          <span className="text-[10px] text-stone-500">{mantraCount} मन्त्र</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowSanskarDialog(false)} className="devanagari-ui">रद्द करें</Button>
            <Button onClick={handleAddSanskars} className="bg-orange-600 hover:bg-orange-700 devanagari-ui">लागू करें</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
